// lib/attribution.js  (BL-038 Story 38.1 — Attribution & Funnel Instrumentation)
//
// The funnel's instrumentation foundation. It does three small, pure-ish things
// on top of the existing PostHog wrapper (lib/analytics) — adding NO new
// dependency and NO billing/payment surface:
//
//   1. Source attribution — parse utm_* off the entry URL and persist it
//      FIRST-TOUCH (a later visit never overwrites the original source), so a
//      conversion can be attributed back to where the visitor came from.
//   2. Plan intent — remember which plan a visitor expressed interest in, so it
//      survives navigation (pricing → waitlist) and the OAuth round-trip
//      (onboarding → activation). plan_interest is a NON-billing demand signal,
//      validated against the canonical tier catalog (never 'pro').
//   3. Funnel events — trackFunnel() tags every funnel-stage event with the
//      persisted attribution so funnel analysis and source attribution share
//      one payload shape.
//
// Persistence is sessionStorage (per-visit, low-sensitivity). It is advisory
// instrumentation only — never a gate, never PII beyond what the waitlist
// already captures. Fails soft everywhere (private-mode / disabled storage).

import { track } from "./analytics";
import { CANONICAL_TIERS } from "./entitlements";

// The five canonical UTM parameters. Anything else on the URL is ignored.
export const UTM_KEYS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]);

const ATTRIBUTION_KEY = "rv-str-attribution";
const PLAN_INTENT_KEY = "rv-str-plan-intent";

// Canonical funnel-stage event names (see BL-038 §2.4 event taxonomy).
export const FUNNEL_EVENTS = Object.freeze({
  PRICING_VIEW: "funnel_pricing_view",
  PLAN_SELECTED: "funnel_plan_selected",
  WAITLIST_SUBMIT: "funnel_waitlist_submit",
  INVITE_PATH: "funnel_invite_path_clicked",
  ONBOARDING_VIEW: "funnel_onboarding_view",
  ACTIVATION_COMPLETE: "funnel_activation_complete",
  UPGRADE_INTENT: "funnel_upgrade_intent",
});

// ── storage helpers (fail-soft) ──────────────────────────────────────────────
function readJson(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — instrumentation is
    // advisory, so silently skip persistence.
  }
}

// ── source attribution ───────────────────────────────────────────────────────

// Pure extraction of the canonical utm_* keys from a query string (or a
// URLSearchParams). Absent/blank values are omitted — no null padding.
export function parseUtmParams(search) {
  const params =
    search instanceof URLSearchParams ? search : new URLSearchParams(search || "");
  const out = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && value.trim()) out[key] = value.trim();
  }
  return out;
}

// Read the persisted first-touch attribution (or {} when none).
export function getAttribution() {
  const stored = readJson(ATTRIBUTION_KEY);
  return stored && typeof stored === "object" ? stored : {};
}

// Capture attribution from the entry URL, FIRST-TOUCH: if attribution is
// already stored it is returned unchanged (the original source wins). Defaults
// to window.location.search; tests pass an explicit `search`.
export function captureAttribution({ search } = {}) {
  const existing = getAttribution();
  if (Object.keys(existing).length > 0) return existing;

  const resolvedSearch =
    search !== undefined
      ? search
      : typeof window !== "undefined" && window.location
      ? window.location.search
      : "";

  const parsed = parseUtmParams(resolvedSearch);
  if (Object.keys(parsed).length > 0) writeJson(ATTRIBUTION_KEY, parsed);
  return parsed;
}

// ── plan intent (non-billing demand signal) ──────────────────────────────────

// Persist a plan intent. Only a canonical tier is accepted (never 'pro'); an
// invalid value stores nothing and returns null.
export function setPlanIntent(tier) {
  if (!CANONICAL_TIERS.includes(tier)) return null;
  writeJson(PLAN_INTENT_KEY, tier);
  return tier;
}

export function getPlanIntent() {
  const stored = readJson(PLAN_INTENT_KEY);
  return CANONICAL_TIERS.includes(stored) ? stored : null;
}

export function clearPlanIntent() {
  try {
    window.sessionStorage.removeItem(PLAN_INTENT_KEY);
  } catch {
    // fail-soft
  }
}

// ── funnel events ─────────────────────────────────────────────────────────────

// Emit a funnel-stage event tagged with the persisted attribution. Explicit
// props win over attribution keys on collision. Delegates to lib/analytics
// (which adds the BL-025 environment tag and no-ops without a PostHog key).
export function trackFunnel(event, props = {}) {
  track(event, { ...getAttribution(), ...props });
}
